import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject, signal, computed} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatDividerModule} from '@angular/material/divider';
import {forkJoin, Subscription} from 'rxjs';

import {ISupportRequest} from '../../../../../interfaces/player/ISupportRequest';
import {IAdminWithRolesAsString} from '../../../../../interfaces/IAdminWithRolesAsString';
import {IResolveSupportRequest} from '../../../../../interfaces/player/IResolveSupportRequest';
import {IAssignAdminToSupportRequest} from '../../../../../interfaces/player/IAssignAdminToSupportRequest';
import {IEscalateSupportRequest} from '../../../../../interfaces/IEscalateSupportRequest';
import {ISupportRequestNote} from '../../../../../interfaces/player/ISupportRequestNote';
import {ICreateSupportRequestNoteCommand} from '../../../../../interfaces/commands/ICreateSupportRequestNoteCommand';
import {ISupportRequestMessage} from '../../../../../interfaces/player/ISupportRequestMessage';
import {ICreateSupportRequestMessageCommand} from '../../../../../interfaces/commands/ICreateSupportRequestMessageCommand';
import {SupportRequestService} from '../../../../../services/support-request.service';
import {ActiveUserService} from '../../../../../services/active-user.service';
import {GameService} from '../../../../../services/game.service';
import {SnackbarService} from '../../../../../services/snackbar.service';
import {SupportTicketSignalRService} from '../../../../../services/support-ticket-signal-r.service';
import {SupportDeskColDefs} from '../../../support-desk-column-defs';
import {UIStateEnum} from '../../../../../enum/UIStateEnum';
import {PermissionTypes} from '../../../../../enum/PermissionTypes';
import {HasPermissionDirective} from '../../../../../directives/permissions/has-permission';

@Component({
  selector: 'app-ticket-detail-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    HasPermissionDirective
  ],
  templateUrl: './ticket-detail-panel.component.html',
  styleUrl: './ticket-detail-panel.component.scss'
})
export class TicketDetailPanelComponent implements OnChanges, OnDestroy {
  private supportRequestService = inject(SupportRequestService);
  private activeUserService = inject(ActiveUserService);
  private gameService = inject(GameService);
  private snackBarService = inject(SnackbarService);
  private supportDeskColDefs = inject(SupportDeskColDefs);
  private signalRService = inject(SupportTicketSignalRService);
  private signalRSubscription: Subscription | null = null;

  @Input() set ticket(value: ISupportRequest | null) {
    this._ticket.set(value);
  }
  @Input() isCrossGameView = false;
  @Input() isOpen = false;

  @Output() closePanel = new EventEmitter<void>();
  @Output() ticketUpdated = new EventEmitter<ISupportRequest>();
  @Output() viewPlayerTickets = new EventEmitter<string>();

  // Internal signal for ticket - allows computed signals to react to changes
  private _ticket = signal<ISupportRequest | null>(null);
  // Public getter for template access
  public get ticket(): ISupportRequest | null { return this._ticket(); }

  public uiState = UIStateEnum.ShowLoading;
  public uiStateEnum = UIStateEnum;
  public permissionTypes = PermissionTypes;

  public adminsToAssign = signal<IAdminWithRolesAsString[]>([]);
  public selectedAssigneeId = '';
  public showResolveForm = false;
  public resolutionDescriptionControl = new FormControl<string>('', Validators.required);

  // Notes
  public notes: ISupportRequestNote[] = [];
  public newNoteText = '';
  public isSubmittingNote = false;

  // Messages
  public messages: ISupportRequestMessage[] = [];
  public newMessageText = '';
  public isSubmittingMessage = false;

  // Computed signals for template bindings
  errorTypeLabel = computed(() => {
    const errorType = this.supportDeskColDefs.errorTypes.find(e => e.value === this._ticket()?.ErrorType);
    return errorType?.name ?? 'Unknown';
  });

  assigneeName = computed(() => {
    const ticket = this._ticket();
    if (!ticket?.Assignee) return 'Unassigned';
    const admin = this.adminsToAssign().find(a => a.Id === ticket.Assignee);
    return admin ? `${admin.FirstName} ${admin.LastName}` : 'Unknown';
  });

  isAssignedToMe = computed(() => {
    return this._ticket()?.Assignee === this.activeUserService.activeUser().Id;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticket'] && this.ticket) {
      this.loadTicketData();
      this.connectToSignalR(this.ticket.Id);
      this.showResolveForm = false;
      this.resolutionDescriptionControl.reset();
      this.newNoteText = '';
      this.newMessageText = '';
    }

    // Disconnect from SignalR when panel is closed
    if (changes['isOpen'] && !this.isOpen) {
      this.signalRSubscription?.unsubscribe();
      this.signalRService.disconnect();
    }
  }

  ngOnDestroy(): void {
    this.signalRSubscription?.unsubscribe();
    this.signalRService.disconnect();
  }

  private connectToSignalR(supportRequestId: string): void {
    const activeUser = this.activeUserService.activeUser();
    const adminName = `${activeUser.FirstName} ${activeUser.LastName}`;
    this.signalRService.connect(supportRequestId, adminName);

    // Unsubscribe from previous subscription if exists
    this.signalRSubscription?.unsubscribe();

    // Subscribe to new messages
    this.signalRSubscription = this.signalRService.newMessage$.subscribe(message => {
      if (message) {
        this.addMessageToList(message);
      }
    });
  }

  private addMessageToList(message: ISupportRequestMessage): void {
    // Check if message already exists (avoid duplicates)
    const messageExists = this.messages.some(m => m.Id === message.Id);
    if (messageExists) return;

    // Add the new message to the list
    this.messages = [...this.messages, message];
  }

  private loadTicketData(): void {
    if (!this.ticket) return;

    this.uiState = UIStateEnum.ShowLoading;

    const gameId = this.isCrossGameView ? undefined : this.gameService.activeGame().Id;

    forkJoin({
      admins: this.supportRequestService.getGameAdminsForSupportRequest(this.ticket.GameId),
      notes: this.supportRequestService.fetchNotesBySupportRequestId(this.ticket.Id, gameId),
      messages: this.supportRequestService.fetchMessagesBySupportRequestId(this.ticket.Id, gameId)
    }).subscribe({
      next: ({admins, notes, messages}) => {
        this.adminsToAssign.set(admins);
        this.notes = notes;
        this.messages = messages;
        if (this.ticket?.Assignee) {
          const currentAdmin = admins.find(a => a.Id === this.ticket?.Assignee);
          if (currentAdmin) {
            this.selectedAssigneeId = currentAdmin.Id;
          }
        }
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to load ticket data');
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  onClose(): void {
    this.closePanel.emit();
  }

  onViewPlayerTickets(): void {
    if (this.ticket?.PlayerEmail) {
      this.viewPlayerTickets.emit(this.ticket.PlayerEmail);
      this.onClose();
    }
  }

  onOverlayClick(event: Event): void {
    if ((event.target as HTMLElement).classList.contains('panel-overlay')) {
      this.onClose();
    }
  }

  assignToMe(): void {
    this.selectedAssigneeId = this.activeUserService.activeUser().Id;
    this.assignRequest();
  }

  assignRequest(): void {
    if (!this.ticket || !this.selectedAssigneeId) return;

    this.uiState = UIStateEnum.ShowRequestProcessing;

    const command: IAssignAdminToSupportRequest = {
      AdminId: this.activeUserService.activeUser().Id,
      RequestId: this.ticket.Id,
      AssigneeId: this.selectedAssigneeId
    };

    if (!this.isCrossGameView) {
      command.GameId = this.gameService.activeGame().Id;
    }

    this.supportRequestService.assignAdminToSupportRequest(command).subscribe({
      next: () => {
        this.snackBarService.openSuccessfulSnackBar('Ticket assigned successfully');
        // Update assignee immediately for responsive UI (optimistic update)
        const currentTicket = this._ticket();
        if (currentTicket) {
          this._ticket.set({ ...currentTicket, Assignee: this.selectedAssigneeId });
        }
        this.refreshTicket();
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to assign ticket');
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  toggleResolveForm(): void {
    this.showResolveForm = !this.showResolveForm;
  }

  resolveTicket(): void {
    if (!this.ticket) return;

    if (!this.resolutionDescriptionControl.valid) {
      this.resolutionDescriptionControl.markAsTouched();
      return;
    }

    this.uiState = UIStateEnum.ShowRequestProcessing;

    const command: IResolveSupportRequest = {
      RequestId: this.ticket.Id,
      AdminId: this.activeUserService.activeUser().Id,
      Resolved: true,
      ResolutionDescription: this.resolutionDescriptionControl.value!
    };

    if (!this.isCrossGameView) {
      command.GameId = this.gameService.activeGame().Id;
    }

    this.supportRequestService.resolveSupportRequest(command).subscribe({
      next: (updatedTicket) => {
        this._ticket.set(updatedTicket);
        this.ticketUpdated.emit(updatedTicket);
        this.snackBarService.openSuccessfulSnackBar('Ticket resolved successfully');
        this.showResolveForm = false;
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to resolve ticket');
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  reopenTicket(): void {
    if (!this.ticket) return;

    this.uiState = UIStateEnum.ShowRequestProcessing;

    const command: IResolveSupportRequest = {
      RequestId: this.ticket.Id,
      AdminId: this.activeUserService.activeUser().Id,
      Resolved: false,
      ResolutionDescription: ''
    };

    if (!this.isCrossGameView) {
      command.GameId = this.gameService.activeGame().Id;
    }

    this.supportRequestService.resolveSupportRequest(command).subscribe({
      next: (updatedTicket) => {
        this._ticket.set(updatedTicket);
        this.ticketUpdated.emit(updatedTicket);
        this.snackBarService.openSuccessfulSnackBar('Ticket reopened');
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to reopen ticket');
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  escalateTicket(): void {
    if (!this.ticket || this.isCrossGameView) return;

    this.uiState = UIStateEnum.ShowRequestProcessing;

    const command: IEscalateSupportRequest = {
      RequestId: this.ticket.Id,
      AdminId: this.activeUserService.activeUser().Id,
      GameId: this.gameService.activeGame().Id,
      Escalate: true
    };

    this.supportRequestService.escalateSupportRequest(command).subscribe({
      next: (updatedTicket) => {
        this._ticket.set(updatedTicket);
        this.ticketUpdated.emit(updatedTicket);
        this.snackBarService.openSuccessfulSnackBar('Ticket escalated to Causable team');
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to escalate ticket');
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  private refreshTicket(): void {
    if (!this.ticket) return;

    const gameId = this.isCrossGameView ? null : this.gameService.activeGame().Id;
    this.supportRequestService.fetchSupportRequestById(this.ticket.Id, gameId).subscribe({
      next: (updatedTicket) => {
        this._ticket.set(updatedTicket);
        this.ticketUpdated.emit(updatedTicket);
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  addNote(): void {
    if (!this.ticket || !this.newNoteText.trim() || this.isSubmittingNote) return;

    this.isSubmittingNote = true;

    const command: ICreateSupportRequestNoteCommand = {
      SupportRequestId: this.ticket.Id,
      Note: this.newNoteText.trim()
    };

    if (!this.isCrossGameView) {
      command.GameId = this.gameService.activeGame().Id;
    }

    this.supportRequestService.createSupportRequestNote(command).subscribe({
      next: () => {
        this.snackBarService.openSuccessfulSnackBar('Note added successfully');
        this.newNoteText = '';
        this.refreshNotes();
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to add note');
        this.isSubmittingNote = false;
      }
    });
  }

  private refreshNotes(): void {
    if (!this.ticket) {
      this.isSubmittingNote = false;
      return;
    }

    const gameId = this.isCrossGameView ? undefined : this.gameService.activeGame().Id;
    this.supportRequestService.fetchNotesBySupportRequestId(this.ticket.Id, gameId).subscribe({
      next: (notes) => {
        this.notes = notes;
        this.isSubmittingNote = false;
      },
      error: () => {
        this.isSubmittingNote = false;
      }
    });
  }

  getNoteAuthorName(createdBy: string): string {
    const admin = this.adminsToAssign().find(a => a.Id === createdBy);
    return admin ? `${admin.FirstName} ${admin.LastName}` : 'Unknown';
  }

  sendMessage(): void {
    if (!this.ticket || !this.newMessageText.trim() || this.isSubmittingMessage) return;

    this.isSubmittingMessage = true;

    const command: ICreateSupportRequestMessageCommand = {
      SupportRequestId: this.ticket.Id,
      Message: this.newMessageText.trim()
    };

    if (!this.isCrossGameView) {
      command.GameId = this.gameService.activeGame().Id;
    }

    this.supportRequestService.createSupportRequestMessage(command).subscribe({
      next: () => {
        this.snackBarService.openSuccessfulSnackBar('Message sent successfully');
        this.newMessageText = '';
        this.isSubmittingMessage = false;
        // Note: The new message will be added automatically via SignalR
        // No need to refresh messages from the server
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to send message');
        this.isSubmittingMessage = false;
      }
    });
  }

  getMessageAuthorName(message: ISupportRequestMessage): string {
    if (message.IsFromPlayer) {
      return this.ticket?.PlayerName ?? 'Player';
    }
    if (message.CreatedByAdminId) {
      const admin = this.adminsToAssign().find(a => a.Id === message.CreatedByAdminId);
      return admin ? `${admin.FirstName} ${admin.LastName}` : 'Support Team';
    }
    return 'Support Team';
  }
}
